import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Link2, Paperclip, ExternalLink } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { PageLoader } from "@/components/ui/Spinner";
import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { fetchTenderClients } from "../api/tenders";
import { useCreateTender } from "../hooks/useTenders";
import { ClientBranchSelector } from "@/features/clients/components/ClientBranchSelector";
import type { TenderFormData } from "../types";

export function TenderNewPage() {
  const navigate = useNavigate();
  const createTender = useCreateTender();

  const { data: clients = [], isLoading: loadingClients } = useQuery({
    queryKey: ["tenders-clients"],
    queryFn: fetchTenderClients,
  });

  const [form, setForm] = useState<TenderFormData>({
    title: "",
    reference_number: "",
    source_url: "",
    client: "",
    client_branch: null,
    client_sub_branch: null,
    description: "",
    offer_deadline: "",
  });
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
    const { name, value } = e.target;
    setForm((f) => {
      const next = { ...f, [name]: value };
      if (name === "client") { next.client_branch = null; next.client_sub_branch = null; }
      return next;
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.title || !form.client || !form.offer_deadline || !form.source_url) return;

    const tender = await createTender.mutateAsync(form);

    // Upload files sequentially
    if (pendingFiles.length > 0) {
      for (let i = 0; i < pendingFiles.length; i++) {
        const file = pendingFiles[i];
        setUploadProgress(`Przesyłanie pliku ${i + 1}/${pendingFiles.length}: ${file.name}`);
        // eslint-disable-next-line no-await-in-loop
        const formData = new FormData();
        formData.append("file", file);
        formData.append("name", file.name);
        const { default: apiClient } = await import("@/api/client");
        await apiClient.post(`/tenders/${tender.id}/attachments/`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
    }

    navigate(`/tenders/${tender.id}`);
  }

  const isSubmitting = createTender.isPending || Boolean(uploadProgress);
  const canSubmit = Boolean(form.title && form.client && form.offer_deadline && form.source_url);

  if (loadingClients) return <PageLoader />;

  return (
    <div className="max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Nowy przetarg</h1>
        <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-6" />
      </div>

      <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 p-6 space-y-4">

        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Tytuł <span className="text-red-500">*</span>
          </label>
          <input
            name="title"
            value={form.title}
            onChange={handleChange}
            required
            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
            placeholder="Nazwa postępowania przetargowego"
          />
        </div>

        {/* Reference number */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Numer referencyjny
          </label>
          <input
            name="reference_number"
            value={form.reference_number}
            onChange={handleChange}
            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
            placeholder="np. ZP/2026/001"
          />
        </div>

        {/* Source URL — required */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            <span className="inline-flex items-center gap-1.5">
              <Link2 size={13} />
              URL ogłoszenia <span className="text-red-500">*</span>
            </span>
          </label>
          <div className="flex gap-2">
            <input
              name="source_url"
              type="url"
              value={form.source_url}
              onChange={handleChange}
              required
              className="flex-1 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
              placeholder="https://ezamowienia.gov.pl/..."
            />
            {form.source_url && (
              <a
                href={form.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 rounded-md border border-gray-300 dark:border-gray-600 text-gray-500 hover:text-[var(--color-primary)] hover:border-[var(--color-primary)] transition-colors"
                title="Otwórz ogłoszenie"
              >
                <ExternalLink size={15} />
              </a>
            )}
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            Link do ogłoszenia przetargowego (e-zamówienia, BZP, strona klienta, itp.)
          </p>
        </div>

        {/* Client */}
        <div>
          <SearchableSelect
            label="Klient *"
            name="client"
            placeholder={loadingClients ? "Ładowanie..." : clients.length === 0 ? "— brak klientów —" : "— wybierz klienta —"}
            value={form.client}
            onChange={handleChange}
            disabled={loadingClients}
            options={clients.map((c) => ({ value: c.id, label: c.name + (c.short_name ? ` (${c.short_name})` : "") }))}
          />
          {!loadingClients && clients.length === 0 && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
              Brak klientów w systemie.{" "}
              <a href="/clients/new" className="underline">Dodaj klienta</a> przed utworzeniem przetargu.
            </p>
          )}
        </div>

        {/* Branch / sub-branch (loaded after client is selected) */}
        <ClientBranchSelector
          clientId={form.client || null}
          selectedBranch={form.client_branch ?? null}
          selectedSubBranch={form.client_sub_branch ?? null}
          onBranchChange={(id) => setForm((f) => ({ ...f, client_branch: id, client_sub_branch: null }))}
          onSubBranchChange={(id) => setForm((f) => ({ ...f, client_sub_branch: id }))}
        />

        {/* Deadline */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Termin składania oferty <span className="text-red-500">*</span>
          </label>
          <input
            name="offer_deadline"
            type="datetime-local"
            value={form.offer_deadline}
            onChange={handleChange}
            required
            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Opis
          </label>
          <textarea
            name="description"
            value={form.description}
            onChange={handleChange}
            rows={4}
            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] resize-none"
            placeholder="Opis postępowania..."
          />
        </div>

        {/* Attachments */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            <span className="inline-flex items-center gap-1.5">
              <Paperclip size={13} />
              Załączniki (opcjonalnie)
            </span>
          </label>

          <FileDropZone
            files={pendingFiles}
            onFilesChange={setPendingFiles}
            multiple
          />
        </div>

        {/* Upload progress */}
        {uploadProgress && (
          <div className="text-xs text-[var(--color-primary)] dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-3 py-2 rounded-md">
            {uploadProgress}
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-2">
          <Button type="button" variant="secondary" onClick={() => navigate(-1)}>
            Anuluj
          </Button>
          <Button type="submit" loading={isSubmitting} disabled={!canSubmit}>
            {pendingFiles.length > 0
              ? `Utwórz przetarg i prześlij ${pendingFiles.length} plik${pendingFiles.length === 1 ? "" : "i"}`
              : "Utwórz przetarg"}
          </Button>
        </div>
      </form>
    </div>
  );
}
